import { matchPath, useLocation } from 'react-router-dom';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsMagic } from '@/hooks/useIsMagic';
import {
  Box,
  Container,
  Link as ChakraLink,
  Text,
  type StyleProps,
} from '@/ui';

const HIDDEN_PATHS = ['/internal/schools', '/dashboard'];

const isExternal = (href: string): boolean => !href.startsWith('/');

export const Banner = () => {
  const { value: bannerMessage } = useFeatureFlag('bannerMessage');
  const { value: bannerLink } = useFeatureFlag('bannerLink');
  const { isMagic } = useIsMagic();
  const { pathname } = useLocation();

  if (HIDDEN_PATHS.some((path) => matchPath(path, pathname) !== null)) {
    return null;
  }

  if (!bannerMessage.trim()) {
    // ConfigCat does not allow an empty string, so we just use whitespace to
    // set it to empty
    return null;
  }

  const textStyle: StyleProps = {
    fontWeight: '500',
    lineHeight: 'normal',
    color: isMagic ? 'black' : 'brand.500',
  };

  return (
    <Box
      display={bannerMessage ? 'block' : 'none'}
      py="2"
      sx={{ '@media print': { display: 'none' } }}
      w="100%"
      {...(isMagic
        ? {
            layerStyle: 'magicBackgroundTertiary',
            border: '1px solid rgba(0,0,0,0.08)',
          }
        : {
            bg: 'white',
            borderBottomColor: 'customgray.gray',
            borderBottomWidth: '1px',
          })}
    >
      <Container
        alignItems="center"
        display="flex"
        fontSize={{ base: 12, sm: 14 }}
        gap={{ base: 3, md: 4 }}
        justifyContent="center"
        variant="max"
      >
        {bannerLink ? (
          <ChakraLink
            {...textStyle}
            href={bannerLink}
            target={isExternal(bannerLink) ? '_blank' : undefined}
          >
            {bannerMessage}
          </ChakraLink>
        ) : (
          <Text {...textStyle}>{bannerMessage}</Text>
        )}
      </Container>
    </Box>
  );
};
